<template>
  <div v-if="dialog" class="modal-overlay">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>{{ $t('homepage.signUpModal.title') }}</h3>
        <button class="close-btn" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form id="register-form" @submit.prevent="submit">
          <v-alert :value="error" type="error" outlined>{{ error }}</v-alert>
          <br />
          <div class="form-row">
            <div class="form-group">
              <label for="firstName">{{ $t('homepage.signUpModal.firstName') }}</label>
              <input
                id="firstName"
                v-model="firstName"
                name="firstName"
                v-validate="'required'"
                type="text"
              />
              <span v-if="errors.first('firstName')" class="error-message">{{
                errors.first("firstName")
              }}</span>
            </div>
            <div class="form-group">
              <label for="lastName">{{ $t('homepage.signUpModal.lastName') }}</label>
              <input
                id="lastName"
                v-model="lastName"
                name="lastName"
                v-validate="'required'"
                type="text"
              />
              <span v-if="errors.first('lastName')" class="error-message">{{
                errors.first("lastName")
              }}</span>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="email">{{ $t('homepage.signUpModal.email') }}</label>
              <input
                id="email"
                v-model="email"
                name="email"
                v-validate="'required|email'"
                type="email"
              />
              <span v-if="errors.first('email')" class="error-message">{{
                errors.first("email")
              }}</span>
            </div>
            <div class="form-group">
              <label for="password">{{ $t('homepage.signUpModal.password') }}</label>
              <input
                id="password"
                v-model="password"
                name="password"
                v-validate="'required|min:8'"
                type="password"
              />
              <span v-if="errors.first('password')" class="error-message">{{
                errors.first("password")
              }}</span>
            </div>
          </div>
          <div class="form-row" v-if="!isUSCG">
            <div class="form-group full-width">
              <label for="phoneNumber">{{ $t('homepage.signUpModal.phoneNumber') }}</label>
              <input id="phoneNumber" v-model="phoneNumber" name="phoneNumber" type="tel" />
              <span v-if="errors.first('phoneNumber')" class="error-message">{{
                errors.first("phoneNumber")
              }}</span>
            </div>
            <div class="form-group full-width">
              <PsapSelect
                v-validate="'required'"
                name="psap"
                :error-messages="errors.first('psap')"
                v-model="psapPrimaryKey"
                outlined
                dense
                hide-details
                solo
                flat
                :menu-props="{ offsetY: true }"
              />
            </div>
          </div>
          <div class="form-row" v-if="psapPrimaryKey === false && !isUSCG">
            <div class="form-group full-width">
              <label for="companyName">{{ $t('homepage.signUpModal.agencyNotListed') }}</label>
              <input
                id="companyName"
                v-validate="'required'"
                name="companyName"
                v-model="companyName"
                :placeholder="$t('homepage.signUpModal.companyPlaceholder')"
                type="text"
              />
              <span v-if="errors.first('companyName')" class="error-message">{{
                errors.first("companyName")
              }}</span>
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; flex-direction: column; align-items: flex-end;">
            <vue-recaptcha
              class="vue-recaptcha"
              @verify="receivedCaptcha"
              sitekey="6LeyYKgrAAAAAPjYH70GZAeKE8Tz7cnnR-rlj9jE"
            />
            <v-flex class="terms-wrapper">
              <p class="terms">
                <v-checkbox
                  class="terms__checkbox"
                  v-model="agreedToTermsOfService"
                  color="primary"
                />
                <span>{{ $t('homepage.signUpModal.agreeTo') }}</span>
                <a @click="tosModalActive = true" class="pink-link">{{ $t('homepage.signUpModal.terms') }}</a>
                <span>{{ $t('homepage.signUpModal.and') }}</span>
                <a @click="privacyPolicyModalActive = true" class="pink-link">{{ $t('homepage.signUpModal.privacyPolicy') }}</a>
                <TosModal v-model="tosModalActive" @accepted="accepted" />
                <PrivacyPolicyModal
                  v-model="privacyPolicyModalActive"
                  @accepted="accepted"
                />
              </p>
            </v-flex>
          </div>
          <div class="form-actions">
            <v-btn type="submit" color="primary" large block :disabled="submitting">{{ $t('homepage.signUpModal.submitButton') }}</v-btn>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import VueRecaptcha from "vue-recaptcha";
import TosModal from "@/modals/TosModal.vue";
import PrivacyPolicyModal from "@/modals/PrivacyPolicyModal.vue";
import store from "@/store";
import PsapSelect from "@/components/PsapSelect.vue";
import {
  registerComplete,
} from "@/services/authentication";

export default Vue.extend({
  name: "SignUpModal",
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      tosModalActive: false,
      privacyPolicyModalActive: false,
      error: null,
      submitting: false,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phoneNumber: "",
      companyName: "",
      agreedToTermsOfService: false,
      captchaToken: "",
      psapPrimaryKey: null,
    };
  },
  computed: {
    smAndDown(): boolean {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isUSCG(): boolean {
      //checks if the domain is USCG
      const USCGDomain = "@uscg.mil";
      return this.email ? this.email.toLowerCase().endsWith(USCGDomain) : false;
    },
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    accepted() {
      this.agreedToTermsOfService = true;
    },
    receivedCaptcha(response: string) {
      this.captchaToken = response;
    },

    closeModal() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.firstName = "";
      this.lastName = "";
      this.email = "";
      this.phoneNumber = "";
      this.password = "";
      this.captchaToken = "";
      this.companyName = "";
      this.psapPrimaryKey = "";
    },
    async submit() {
      const isValid = await this.$validator.validateAll();
      if (isValid && !this.submitting) {
        try {
          this.submitting = true;
          this.error = null;
          await store.dispatch("authentication/register", {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            phoneNumber: this.isUSCG ? null : this.phoneNumber,
            password: this.password,
            captchaToken: this.captchaToken,
            agreedToTermsOfService: this.agreedToTermsOfService,
          });
          //if non uscg replace register-complete page with this call
          if (!this.isUSCG) {
            const response = await registerComplete({
              companyName: this.companyName,
              psapPrimaryKey: this.psapPrimaryKey,
            });

            // Get the most up to date user information into store
            await store.dispatch("authentication/getCurrentUser");

            // Add user to 'Unaffiliated Users' if any of these values are present
            if (this.psapPrimaryKey === false || this.companyName === null)
              await store.dispatch("authentication/joinUnlisted");

            this.$router.push({ name: "dashboard" });
            // Refreshing the current page without waiting
            setTimeout(() => {
              location.reload();
            }, 100);
          }
          //if uscg redirect to the standard register-complete-uscg page
          else {
            this.$router.push({ name: "register-complete-uscg" });
          }

          // @ts-ignore
        } catch ({ response }) {
          if (response && response.data && response.data.error) {
            this.error = response.data.error;
          } else {
            this.error = this.$t('homepage.signUpModal.errorGeneric');
          }
        }
        this.submitting = false;
      }
    },
  },
  components: {
    VueRecaptcha,
    TosModal,
    PrivacyPolicyModal,
    PsapSelect,
  },
});
</script>

<style lang="scss" scoped>
.terms-wrapper {
  margin: -20px 0;
}

.terms {
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: left;

  & > * {
    margin-right: 0.2em;
  }
}

.terms__checkbox {
  flex: none;
  margin-top: 20px;
}

.vue-recaptcha {
  margin-bottom: 1.3rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;
}

.modal-content {
  background: #fefefe;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  background-color: #1876d1;
  color: white;
  padding: 0.75rem;
  border-radius: 10px 10px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.155rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.close-btn {
  color: white;
  font-size: 1.54rem;
  cursor: pointer;
  padding: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.modal-body {
  padding: 2rem;
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #1f2937;
  font-size: 0.67375rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.input-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.77rem;
  font-family: inherit;
  background: #f9fafb;
  color: #1f2937;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.input-group input:focus {
  outline: none;
  border-color: #1876d1;
  background: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.5rem;
}

.form-row {
  display: flex;
  gap: 1.5rem;
}

.form-group {
  flex: 1;
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #1f2937;
  font-size: 0.67375rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.77rem;
  font-family: inherit;
  background: #f9fafb;
  color: #1f2937;
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #1876d1;
  background: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 0.77rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-width: 120px;
}

.btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:hover::before {
  left: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #1876d1, #1876d1);
  color: white;
  box-shadow: 0 4px 15px rgba(30, 64, 175, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1876d1, #1876d1);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(30, 64, 175, 0.4);
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.form-group ::v-deep .v-input__slot {
  height: 42px;
  padding: 0.75rem;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}

.recaptcha-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}
</style>
